'use client'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface DropzoneProps {
  accept: string
  helpText: string
  file: File | null
  onFileChange: (file: File) => void
}

export function Dropzone({ accept, helpText, file, onFileChange }: DropzoneProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-1.5 rounded-lg border-2 border-dashed p-5 text-center transition-colors',
        dragOver ? 'border-primary bg-primary/5' : 'border-border',
      )}
      role="button"
      tabIndex={0}
      aria-label="Upload document"
      onClick={() => fileInputRef.current?.click()}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && fileInputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault()
        setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragOver(false)
        const dropped = e.dataTransfer.files?.[0]
        if (dropped) onFileChange(dropped)
      }}
    >
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-muted-foreground"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
      <span className="text-foreground text-sm font-medium">
        {file?.name ?? 'Drop your file here'}
      </span>
      {!file && (
        <span className="text-muted-foreground text-sm">
          or{' '}
          <span className="text-primary underline-offset-4 hover:underline">
            click to browse files
          </span>
        </span>
      )}
      <span className="text-muted-foreground text-xs">{helpText}</span>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(e) => {
          const picked = e.target.files?.[0]
          if (picked) onFileChange(picked)
        }}
      />
    </div>
  )
}
